import { isValidPhone, parsePhone } from "@vyne-shared/utils/phone";
import { mapFreeze } from "utils/maps";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { PhoneNumber } from "react-phone-number-input";

/**
 * Parse the phone number from a Bulk Request record.
 * @param record The record to examine.
 * @returns The phone number in a valid and formatted form, or undefined if the number does not exist or could not be
 * parsed. The phone number can be used for display, or evaluated for truthiness: truthy means valid, falsy means
 * invalid.
 * When input is valid, result is of the form "(212) 555-0100".
 */
export function validatePhoneNational(record: BulkRequestRecord): string | undefined {
    return vpDispatch(record, NationalTransform);
}

/**
 * Parse the phone number from a Bulk Request record.
 * @param record The record to examine.
 * @returns True when the phone number on the input record was valid. Otherwise, false.
 */
export function validatePhoneBoolean(record: BulkRequestRecord): boolean {
    return !!(vpDispatch(record, BooleanTransform) satisfies boolean | undefined);
}

/**
 * Parse the phone number from a Bulk Request record.
 * @param record The record to examine.
 * @returns The phone number in a valid and formatted form, or undefined if the number was not valid.
 * When input is valid, result is of the form "+12125550100".
 */
export function validatePhoneString(record: BulkRequestRecord): string | undefined {
    return vpDispatch(record, StringTransform);
}

const NationalTransform: unique symbol = Symbol("NationalTransform");
const BooleanTransform: unique symbol = Symbol("BooleanTransform");
const StringTransform: unique symbol = Symbol("StringTransform");

type PhoneTransforms = typeof phoneTransforms;
const phoneTransforms = Object.freeze({
    [NationalTransform]: (phone: PhoneNumber) => phone.formatNational(),
    [BooleanTransform]: () => true,
    [StringTransform]: (phone: PhoneNumber) => phone.number,
});

const vpdcCache = mapFreeze(
    new Map(
        Object.getOwnPropertySymbols(phoneTransforms).map(
            (x) => [x, new WeakMap<BulkRequestRecord, unknown>()] as const,
        ),
    ),
);

function vpDispatch<T extends keyof PhoneTransforms>(record: BulkRequestRecord, transformKey: T) {
    const innerCache = vpdcCache.get(transformKey)!;

    if (innerCache.has(record)) {
        return innerCache.get(record) as ReturnType<PhoneTransforms[T]> | undefined;
    }

    const result = vpDispatchCore(record, transformKey);
    innerCache.set(record, result);
    return result as ReturnType<PhoneTransforms[T]> | undefined;
}

function vpDispatchCore(record: BulkRequestRecord, transformKey: keyof typeof phoneTransforms) {
    const phone = validatePhoneParser(record);
    if (!phone) {
        return undefined;
    }

    return phoneTransforms[transformKey](phone);
}

const vppCache = new WeakMap<BulkRequestRecord, PhoneNumber | undefined>();

function validatePhoneParser(record: BulkRequestRecord) {
    if (vppCache.has(record)) {
        return vppCache.get(record);
    }

    const result = validatePhoneParserCore(record);
    vppCache.set(record, result);
    return result;
}

function validatePhoneParserCore(record: BulkRequestRecord) {
    const phone = parsePhone(record.phone);
    if (!phone) {
        return undefined;
    }

    // we could check phone.isValid, which is actually stricter than our isValidPhone regex (for example, it rejects some bad area codes like 555),
    // but let's stick to isValidPhone, which was easier to integrate with the phone input control, for consistency
    if (!isValidPhone(phone.number)) {
        return undefined;
    }

    return phone;
}
